import React, { useEffect, useMemo, useRef, useState } from 'react';
import { animate, motion, useInView } from 'framer-motion';

export const ACCENT = '#8fdc6b';
export const ACCENT_SOFT = 'rgba(143, 220, 107, 0.16)';
export const MUTED = '#8b9588';
export const AMBER = '#f5c451';
export const ROSE = '#f3b4a8';
export const BLUE = '#7cc4ff';

export const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export function Reveal({ children, delay = 0, className = '', style, as = 'div', once = true, ...rest }) {
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      style={style}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.2 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function parseMoney(display) {
  if (typeof display === 'number') return display;
  const clean = String(display || '0').replace(/[^0-9.-]/g, '');
  const value = Number.parseFloat(clean);
  return Number.isFinite(value) ? value : 0;
}

export function formatMoney(value, { compact = false } = {}) {
  const abs = Math.abs(value);
  if (compact && abs >= 1000) {
    return `$${(value / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  }
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function CountUp({ value, prefix = '', suffix = '', decimals = 2, duration = 1.2, className = '', style, format }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const target = typeof value === 'number' ? value : parseMoney(value);
  const render = (latest) => (format
    ? format(latest)
    : `${prefix}${latest.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`);
  const [text, setText] = useState(() => render(0));
  const started = useRef(false);
  useEffect(() => {
    if (!inView) return undefined;
    const from = started.current ? parseMoney(text) : 0;
    started.current = true;
    const controls = animate(from, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => setText(render(latest)),
    });
    return () => controls.stop();
  }, [inView, target]);
  return <span ref={ref} className={className} style={style}>{text}</span>;
}

export function MoneyCount({ display, className = '', style, duration = 1.2 }) {
  const value = parseMoney(display);
  return <CountUp value={value} prefix="$" decimals={2} duration={duration} className={className} style={style} />;
}

export function ProgressBar({ value = 0, color = ACCENT, height = 8, track = 'rgba(232,239,230,0.08)', delay = 0, className = '' }) {
  return (
    <div className={`np-progress ${className}`} style={{ height, background: track }}>
      <motion.div
        className="np-progress-fill"
        style={{ background: color }}
        initial={{ width: 0 }}
        whileInView={{ width: `${Math.max(0, Math.min(100, value))}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      />
    </div>
  );
}

export function Sparkline({ points = [], color = ACCENT, height = 56, width = 220, fill = true, strokeWidth = 2 }) {
  const path = useMemo(() => {
    if (!points.length) return { line: '', area: '' };
    const max = Math.max(...points, 1);
    const min = Math.min(...points, 0);
    const span = max - min || 1;
    const stepX = points.length > 1 ? width / (points.length - 1) : width;
    const coords = points.map((p, i) => [i * stepX, height - 4 - ((p - min) / span) * (height - 8)]);
    const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${line} L${width},${height} L0,${height} Z`;
    return { line, area, coords };
  }, [points, height, width]);
  const gradientId = useMemo(() => `np-spark-${Math.random().toString(36).slice(2, 8)}`, []);
  if (!points.length) return null;
  return (
    <svg className="np-sparkline" viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill ? (
        <motion.path d={path.area} fill={`url(#${gradientId})`} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }} />
      ) : null}
      <motion.path
        d={path.line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
      {path.coords?.length ? (
        <motion.circle
          cx={path.coords[path.coords.length - 1][0]}
          cy={path.coords[path.coords.length - 1][1]}
          r="3.5"
          fill={color}
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.1, duration: 0.3 }}
        />
      ) : null}
    </svg>
  );
}

export function BarChart({ data = [], valueKey = 'value', labelKey = 'label', color = ACCENT, secondaryKey, secondaryColor = 'rgba(232,239,230,0.22)', height = 120, money = true, emptyLabel = 'No data yet' }) {
  const max = Math.max(...data.map((d) => Math.max(Number(d[valueKey]) || 0, Number(secondaryKey ? d[secondaryKey] : 0) || 0)), 1);
  const [hover, setHover] = useState(null);
  const total = data.reduce((sum, d) => sum + (Number(d[valueKey]) || 0), 0);
  return (
    <div className="np-bars" style={{ height }}>
      {!total && !data.some((d) => Number(secondaryKey ? d[secondaryKey] : 0)) ? (
        <div className="np-bars-empty">{emptyLabel}</div>
      ) : null}
      {data.map((row, index) => {
        const value = Number(row[valueKey]) || 0;
        const secondary = secondaryKey ? Number(row[secondaryKey]) || 0 : 0;
        return (
          <div
            key={row[labelKey] || index}
            className={`np-bar-col ${hover === index ? 'on' : ''}`}
            onMouseEnter={() => setHover(index)}
            onMouseLeave={() => setHover(null)}
          >
            <div className="np-bar-stack">
              {secondaryKey ? (
                <motion.div
                  className="np-bar np-bar-secondary"
                  style={{ background: secondaryColor, minHeight: secondary ? 2 : 0 }}
                  initial={{ height: 0 }}
                  whileInView={{ height: `${(secondary / max) * 100}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
                />
              ) : null}
              <motion.div
                className="np-bar"
                style={{ background: color, minHeight: value ? 2 : 0 }}
                initial={{ height: 0 }}
                whileInView={{ height: `${(value / max) * 100}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <span className="np-bar-label">{row[labelKey]}</span>
            {hover === index ? (
              <div className="np-bar-tip">
                {money ? formatMoney(value / 100) : value}
                {secondaryKey ? <span className="np-muted"> · {money ? formatMoney(secondary / 100) : secondary}</span> : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function Donut({ segments = [], size = 132, thickness = 14, centerLabel, centerValue }) {
  const total = segments.reduce((sum, s) => sum + (Number(s.value) || 0), 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="np-donut" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(232,239,230,0.08)" strokeWidth={thickness} />
        {total ? segments.map((segment, index) => {
          const fraction = (Number(segment.value) || 0) / total;
          const dash = fraction * circumference;
          const circle = (
            <motion.circle
              key={segment.label || index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={segment.color || ACCENT}
              strokeWidth={thickness}
              strokeLinecap="butt"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
              initial={{ opacity: 0, strokeDasharray: `0 ${circumference}` }}
              whileInView={{ opacity: 1, strokeDasharray: `${dash} ${circumference - dash}` }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: index * 0.12, ease: 'easeOut' }}
            />
          );
          offset += dash;
          return circle;
        }) : null}
      </svg>
      <div className="np-donut-center">
        <strong>{centerValue}</strong>
        {centerLabel ? <span>{centerLabel}</span> : null}
      </div>
    </div>
  );
}

export function Funnel({ steps = [], color = ACCENT }) {
  const max = Math.max(...steps.map((s) => Number(s.count) || 0), 1);
  return (
    <div className="np-funnel">
      {steps.map((step, index) => (
        <div className="np-funnel-row" key={step.id || index}>
          <span className="np-funnel-label">{step.label}</span>
          <div className="np-funnel-track">
            <motion.div
              className="np-funnel-fill"
              style={{ background: color, opacity: 1 - index * 0.11 }}
              initial={{ width: 0 }}
              whileInView={{ width: `${Math.max(step.count ? 6 : 0, ((Number(step.count) || 0) / max) * 100)}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <strong className="np-funnel-count">{step.count}</strong>
        </div>
      ))}
    </div>
  );
}

export function Glow({ className = '' }) {
  return (
    <div className={`np-glow ${className}`} aria-hidden="true">
      <motion.span
        className="np-glow-orb a"
        animate={{ x: [0, 40, -20, 0], y: [0, -30, 20, 0], scale: [1, 1.12, 0.96, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.span
        className="np-glow-orb b"
        animate={{ x: [0, -50, 30, 0], y: [0, 30, -20, 0], scale: [1, 0.92, 1.08, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}

export function useTicker(count, interval = 2600, enabled = true) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!enabled || count <= 1) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), interval);
    return () => clearInterval(timer);
  }, [count, interval, enabled]);
  return index;
}

export function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function shortDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
